import { Box, Flex, HStack, ModalBody, VStack, useDisclosure, type StackProps } from "@chakra-ui/react";
import {
  ArrowRightIcon,
  Button,
  EthTokenIcon,
  HelpFilledIcon,
  InformationCircleFilledIcon,
  LowEthIcon,
  Modal,
  ModalHeaderLegacy,
  NextLink,
  Text,
  type ButtonProps,
  type ModalProps,
  type TextProps,
} from "@looksrare/uikit";
import { useTranslation } from "next-i18next";
import { gradients } from "@/theme/colors";

interface LimitedModeModalProps {
  isOpen: ModalProps["isOpen"];
  onClose: ModalProps["onClose"];
}

const ExplanationText = (props: TextProps) => {
  const { t } = useTranslation();
  return (
    <Text as="p" color="text-03" {...props}>
      {t(
        "yolo::This Limited version of YOLO has set entry limits, making a friendlier experience for new or more risk-averse players."
      )}
    </Text>
  );
};

const MinEntry = (props: StackProps) => {
  const { t } = useTranslation();
  return (
    <HStack justifyContent="end" {...props}>
      <Text color="text-03">{t("yolo::Min Entry")}:</Text>
      <Text fontWeight={400}>0.001</Text>
      <EthTokenIcon boxSize={4} />
    </HStack>
  );
};

const MaxEntry = (props: StackProps) => {
  const { t } = useTranslation();
  return (
    <HStack justifyContent="end" {...props}>
      <Text color="text-03">{t("yolo::Max Entry")}:</Text>
      <Text fontWeight={400}>0.1</Text>
      <EthTokenIcon boxSize={4} />
    </HStack>
  );
};

const NoLimitButtonLink = (props: ButtonProps) => {
  const { t } = useTranslation();
  return (
    <Button as={NextLink} href="/yolo" variant="outline" rightIcon={<ArrowRightIcon />} width="100%" {...props}>
      {t("yolo::Switch to No Limits")}
    </Button>
  );
};

const LimitedModeModal = ({ isOpen, onClose }: LimitedModeModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalContentProps={{ bgGradient: gradients["ui-gradient-v-01"] }}>
      <ModalHeaderLegacy onClose={onClose} showCloseButton />
      <ModalBody pt={0}>
        <VStack alignItems="center" spacing={4}>
          <LowEthIcon color="link-01" boxSize={16} />
          <Text bold textStyle="header-03">
            {t("yolo::Limited Mode")}
          </Text>
          <ExplanationText textAlign="center" />
          <Box>
            <MinEntry justifyContent="center" />
            <MaxEntry justifyContent="center" />
          </Box>
          <VStack spacing={2} width="100%">
            <Button width="100%" colorScheme="brand" onClick={onClose}>
              {t("yolo::Let's Play!")}
            </Button>
            <NoLimitButtonLink />
          </VStack>
        </VStack>
      </ModalBody>
    </Modal>
  );
};

export const LimitedModeExplanationCard = () => {
  const disclosure = useDisclosure();
  const { t } = useTranslation();
  return (
    <Flex
      alignItems="center"
      borderRadius="dialog"
      p={4}
      bgGradient={gradients["ui-gradient-v-01"]}
      border="1px solid"
      borderColor="border-01"
      height="100%"
    >
      <Box flex={1}>
        <Flex alignItems="center" justifyContent="space-between">
          <HStack flex={1}>
            <InformationCircleFilledIcon boxSize={5} color="link-01" />
            <Text color="link-01" bold>
              {t("yolo::Limited Mode")}
            </Text>
          </HStack>
          <Box display={{ base: "block", lg: "none" }}>
            <Button size="xs" width="full" colorScheme="secondary" onClick={() => disclosure.onOpen()}>
              <HelpFilledIcon boxSize={5} mr={1} /> {t("Learn More")}
            </Button>
          </Box>
          <LimitedModeModal isOpen={disclosure.isOpen} onClose={disclosure.onClose} />
        </Flex>
        <Box display={{ base: "none", lg: "block" }} mt={4}>
          <HStack spacing={8} mb={4}>
            <ExplanationText />
            <Box minWidth="160px">
              <MinEntry />
              <MaxEntry />
            </Box>
          </HStack>
          <Flex alignItems="center" justifyContent="space-between">
            <Text bold flex={1}>
              {t("yolo::Ready for the next level of YOLO?")}
            </Text>
            <Box justifySelf="end" flex={1}>
              <NoLimitButtonLink />
            </Box>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};
